import { useEffect } from 'react';

import { difference, intersection, isEmpty, isEqual, keyBy, maxBy } from 'lodash';

import usePrevious from 'common/helpers/usePrevious';

export type UseEntityListWatcherProps<EntityResponse> = {
  skip?: boolean;
  name?: string;
  loading: boolean;
  entities: EntityResponse[];
  staleEntities: EntityResponse[];
  syncOnDelete?: boolean;
  onEntityLoaded?: (loadedEntity: EntityResponse) => void;
  onEntityCreated: (newEntity: EntityResponse) => void;
  onEntityUpdated: (currEntity: EntityResponse, prevEntity: EntityResponse) => void;
  onEntityDeleted: (removedEntity: EntityResponse) => void;
  selectEntityId: (entity: EntityResponse) => string;
  selectSyncOn: (entity: EntityResponse) => Date;
};

export type UseEntityListWatcherHook<EntityResponse> = (props: UseEntityListWatcherProps<EntityResponse>) => void;
/**
 * The that monitors an array of entities returned by a query, and call appropriate callbacks if
 * some entities are added/updated/removed from that array.
 */
export default function useEntityListWatcher<EntityResponse>({
  skip,
  loading,
  entities: currEntities,
  staleEntities,
  syncOnDelete,
  onEntityCreated,
  onEntityLoaded,
  onEntityDeleted,
  onEntityUpdated,
  selectEntityId,
  selectSyncOn,
}: UseEntityListWatcherProps<EntityResponse>) {
  const previousLoading = usePrevious(loading);
  const prevEntities = usePrevious(currEntities);

  useEffect(() => {
    if (skip) return;

    // Some entities are loaded initially. Call the `onEntityLoaded` for each one of them.
    if (previousLoading && !loading && isEmpty(prevEntities) && onEntityLoaded) {
      currEntities.forEach(entity => onEntityLoaded(entity));
      return;
    }

    // The entities have been updated.
    if (!isEqual(currEntities, prevEntities)) {
      const currEntitiesMap = keyBy(currEntities, selectEntityId);
      const prevEntitiesMap = keyBy(prevEntities, selectEntityId);
      const currEntityIds = Object.keys(currEntitiesMap);
      const prevEntityIds = Object.keys(prevEntitiesMap);

      const newEntities = difference(currEntityIds, prevEntityIds).map(id => currEntitiesMap[id]);
      const deletedEntityIds = difference(prevEntityIds, currEntityIds);

      let deletedEntities: EntityResponse[] = [];

      if (syncOnDelete) {
        // If sync is enabled we prioritize reading state from sync response, if present
        const syncedStaleEntitiesMap = keyBy(staleEntities, selectEntityId);

        deletedEntities = deletedEntityIds.map(
          id => maxBy([syncedStaleEntitiesMap[id] || prevEntitiesMap[id], prevEntitiesMap[id]], selectSyncOn)!
        );
      } else {
        deletedEntities = deletedEntityIds.map(id => prevEntitiesMap[id]);
      }

      const updatedEntityPairs = intersection(prevEntityIds, currEntityIds)
        .map(id => [currEntitiesMap[id], prevEntitiesMap[id]])
        .filter(([curr, prev]) => !isEqual(curr, prev));

      newEntities.forEach(entity => onEntityCreated(entity));
      deletedEntities.forEach(entity => onEntityDeleted(entity));
      updatedEntityPairs.forEach(([curr, prev]) => onEntityUpdated(curr, prev));
    }
  }, [
    currEntities,
    prevEntities,
    loading,
    previousLoading,
    onEntityCreated,
    onEntityDeleted,
    onEntityUpdated,
    onEntityLoaded,
    selectEntityId,
    selectSyncOn,
    staleEntities,
    syncOnDelete,
    skip,
  ]);
}
